import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { Link, useNavigate } from 'react-router-dom';
import inventoryService from '../services/inventoryService';
import salesService from '../services/salesService';
import loyaltyService from '../services/loyaltyService';
import promotionsService from '../services/promotionsService';
import settingsService from '../services/settingsService';
import storeService from '../services/storeService';
import b2bOrderService from '../services/b2bOrderService';
// import configService from '../services/configService'; // Removed as it's not used
import Cart from '../components/checkout/Cart';
import CustomerSearchModal from '../components/checkout/CustomerSearchModal';
import PaymentModal from '../components/checkout/PaymentModal';
import ReceiptModal from '../components/checkout/ReceiptModal';
import SkuSearchModal from '../components/checkout/SkuSearchModal';
import ProductSearchInput from '../components/checkout/ProductSearchInput'; // Import the new component
import PriceEntryModal from '../components/checkout/PriceEntryModal';
import QuickPickModal from '../components/checkout/QuickPickModal';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import { useSocket } from '../context/SocketContext'; // Corrected import path
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import {
  updateCustomerDisplay,
  clearCustomerDisplay,
  showIdleCustomerDisplay,
} from '../utils/customerDisplayBridge';
import { buildCheckoutSaleItems } from '../utils/checkoutPayload';
import tillService from '../services/tillService';
import TerminalSetupPage from './TerminalSetupPage';
import CashierQuickActions from '../components/common/CashierQuickActions';

export const resolveProductPrice = (product) => {
  if (!product) return 0;
  const override = Number(product?.storePriceOverride?.price);
  if (product?.storePriceOverride?.isActive && !Number.isNaN(override) && override > 0) return override;
  const direct = Number(product.price);
  if (!Number.isNaN(direct) && direct > 0) return direct;
  const base = Number(product?.basePrice);
  if (!Number.isNaN(base) && base > 0) return base;
  return 0;
};

const SALES_APPLY_DISCOUNT_PERMISSION = 'sales:apply_discount';

const createCheckoutIdempotencyKey = () => {
  const browserCrypto = typeof window !== 'undefined' ? window.crypto : null;
  const randomUUID = browserCrypto?.randomUUID;
  if (typeof randomUUID === 'function') {
    return randomUUID.call(browserCrypto);
  }
  return `checkout-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const canUserApplyCheckoutDiscount = (user) =>
  Boolean(user?.permissions?.includes(SALES_APPLY_DISCOUNT_PERMISSION));

export const normalizeCheckoutDiscountReasons = (reasons) => (
  Array.isArray(reasons)
    ? reasons.filter((reason) => reason?.id && reason?.name)
    : []
);

export const resolveCartDiscountReasonId = (payload) => (
  payload.mode === 'none' ? null : payload.discountReasonId || null
);
export const buildQuickPickProductQuery = ({ category = '', subcategory = '' } = {}) => ({
  page: 1,
  limit: 200,
  category: category.trim() || undefined,
  subcategory: subcategory.trim() || undefined,
  quickPickOnly: true,
});

const normalizeQuickPickText = (value) => (
  typeof value === 'string' ? value.trim().toLowerCase() : ''
);

export const buildProductQuickPickNavigation = (products = []) => {
  const categoryGroups = new Map();
  const directProducts = [];

  products.forEach((product) => {
    if (!product?._id || !product.quickPickEnabled) return;

    const rawCategoryName = typeof product.category === 'string'
      ? product.category.trim()
      : product.category?.name?.trim();
    const categoryKey = normalizeQuickPickText(rawCategoryName);
    if (!categoryKey) {
      directProducts.push({ key: product._id, product });
      return;
    }

    const subcategory = typeof product.subcategory === 'string'
      ? product.subcategory.trim()
      : '';
    if (!categoryGroups.has(categoryKey)) {
      categoryGroups.set(categoryKey, {
        _id: `quick-pick-category-${categoryKey}`,
        name: rawCategoryName,
        subcategories: new Map(),
      });
    }
    if (subcategory) {
      categoryGroups.get(categoryKey).subcategories.set(
        normalizeQuickPickText(subcategory),
        subcategory
      );
    }
  });

  const quickPickCategories = [...categoryGroups.values()]
    .map((category) => ({
      ...category,
      subcategories: [...category.subcategories.values()].sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return {
    categories: quickPickCategories,
    products: directProducts,
  };
};

const CheckoutPage = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [cart, setCart] = useState([]);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // const [success, setSuccess] = useState(''); // Removed as it's not used
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isCustomerModalOpen, setCustomerModalOpen] = useState(false);
  const [salePreview, setSalePreview] = useState(null);
  const [isPaymentModalOpen, setPaymentModalOpen] = useState(false); 
  const [isReceiptModalOpen, setReceiptModalOpen] = useState(false);
  const [completedSale, setCompletedSale] = useState(null);
  const [tyroReceipts, setTyroReceipts] = useState(null);
  const [isSkuModalOpen, setIsSkuModalOpen] = useState(false);
  const [failedSku, setFailedSku] = useState('');
  const [loyaltyRedemption, setLoyaltyRedemption] = useState({ points: 0, discountAmount: 0 });
  const [loyaltyRatio, setLoyaltyRatio] = useState(100);
  const [loyaltyOriginalBalance, setLoyaltyOriginalBalance] = useState(null);
  const [discountReasons, setDiscountReasons] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [selectedPromotionIds, setSelectedPromotionIds] = useState([]);
  const [promoMessage, setPromoMessage] = useState('');
  const [isCfdPaired, setIsCfdPaired] = useState(false);
  const [priceModalItem, setPriceModalItem] = useState(null);
  const [showTerminalSetup, setShowTerminalSetup] = useState(false);
  const [showTillClosedModal, setShowTillClosedModal] = useState(false);
  const [tillCheckError, setTillCheckError] = useState('');
  const [searchSelectionIndex, setSearchSelectionIndex] = useState(-1);
  const [quickPickCategories, setQuickPickCategories] = useState([]);
  const [quickPickProductActions, setQuickPickProductActions] = useState([]);
  const [quickPickCategory, setQuickPickCategory] = useState(null);
  const [quickPickInitialSubcategory, setQuickPickInitialSubcategory] = useState('');
  const [quickPickProducts, setQuickPickProducts] = useState([]);
  const [quickPickSelectedProduct, setQuickPickSelectedProduct] = useState(null);
  const [quickPickLoading, setQuickPickLoading] = useState(false);
  const [quickPickError, setQuickPickError] = useState('');
  const [checkoutNotice, setCheckoutNotice] = useState(null);
  const [isCreatingB2BOrder, setIsCreatingB2BOrder] = useState(false);
  const [isFinalizingSale, setIsFinalizingSale] = useState(false);
  const [tillName, setTillName] = useState('');
  const [tillNumber, setTillNumber] = useState('');
  const searchInputRef = useRef(null);
  const saleFinalizationInFlightRef = useRef(false);
  const checkoutIdempotencyKeyRef = useRef(createCheckoutIdempotencyKey());
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const getCartItemKey = useCallback((item) => item.lineId || item.productId, []);

  const socket = useSocket();
  const { activeStore } = useStore();
  const { tillId, user } = useAuth();
  const navigate = useNavigate();
  const canApplyDiscount = canUserApplyCheckoutDiscount(user);
  const canCreateB2BOrder = Boolean(
    activeStore?.checkoutB2BOrderEnabled && user?.permissions?.includes('b2b_orders:create')
  );
  const resetCheckoutIdempotencyKey = useCallback(() => {
    checkoutIdempotencyKeyRef.current = createCheckoutIdempotencyKey();
  }, []);
  const runRemoteSearch = useCallback(
    async (term) => {
      if (!activeStore || !term?.trim()) {
        setSearchResults([]);
        setSearchSelectionIndex(-1);
        return;
      }
      try {
        setSearchLoading(true);
        const data = await inventoryService.searchProducts(activeStore._id, term.trim());
        setSearchResults(data);
        setSearchSelectionIndex(data.length ? 0 : -1);
      } catch (err) {
        console.error('Product search failed:', err);
        setError('Failed to search products for this store.');
        setSearchResults([]);
        setSearchSelectionIndex(-1);
      } finally {
        setSearchLoading(false);
      }
    },
    [activeStore]
  );

  const clearCart = useCallback(() => {
    setCart([]);
    setSelectedCustomer(null);
    setLoyaltyRedemption({ points: 0, discountAmount: 0 });
    setLoyaltyOriginalBalance(null);
    setSelectedPromotionIds([]);
    resetCheckoutIdempotencyKey();
  }, [resetCheckoutIdempotencyKey]);

  useEffect(() => {
    // When the active store changes, clear the cart and the product list
    // to prevent adding items from a previously selected store.
    clearCart();
    setSearchResults([]);
    setSearchTerm('');
    setSearchSelectionIndex(-1);
    setCheckoutNotice(null);
    setShowTerminalSetup(!tillId);
    setQuickPickCategory(null);
    setQuickPickInitialSubcategory('');
  }, [activeStore, clearCart, tillId]);

  useEffect(() => {
    const checkTillSession = async () => {
      setTillCheckError('');
      if (!tillId) {
        setShowTillClosedModal(false);
        return;
      }
      try {
        await tillService.getOpenSession(tillId);
        setShowTillClosedModal(false);
      } catch (err) {
        setShowTillClosedModal(true);
        setTillCheckError(err?.response?.data?.message || 'Till is not open.');
      }
    };
    checkTillSession();
  }, [tillId]);

  useEffect(() => {
    let isMounted = true;
    const loadTillName = async () => {
      if (!activeStore || !tillId) {
        if (isMounted) {
          setTillName('');
          setTillNumber('');
        }
        return;
      }
      try {
        const storeDetails = await storeService.getStoreById(activeStore._id);
        const matchingTill = storeDetails?.tills?.find((till) => till._id === tillId);
        if (isMounted) {
          setTillName(matchingTill?.name || '');
          setTillNumber(matchingTill?.tid || '');
        }
      } catch (err) {
        if (isMounted) {
          setTillName('');
          setTillNumber('');
        }
      }
    };
    loadTillName();
    return () => {
      isMounted = false;
    };
  }, [activeStore, tillId]);

  const fetchPromotionsForStore = useCallback(async () => {
    if (!activeStore) return;
    try {
      const data = await promotionsService.getActivePromotions(activeStore._id);
      const filtered = data.filter(
        (promo) =>
          promo.isActive &&
          (promo.type === 'PERCENTAGE_ORDER' || promo.type === 'FIXED_AMOUNT_ORDER')
      );
      setPromotions(filtered);
    } catch (err) {
      console.error('Failed to fetch promotions', err);
    }
  }, [activeStore]);

  useEffect(() => {
    if (!activeStore) {
      setPromotions([]);
      setSelectedPromotionIds([]);
      return;
    }
    fetchPromotionsForStore();
  }, [activeStore, fetchPromotionsForStore]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await settingsService.getSettings();
        const ratio = Number(settings?.loyaltyPointsToDollarRatio);
        if (Number.isFinite(ratio) && ratio > 0) {
          setLoyaltyRatio(ratio);
        }
      } catch (err) {
        console.warn('Failed to load settings for loyalty ratio:', err);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchDiscountReasons = async () => {
      try {
        const reasons = await settingsService.getDiscountReasons();
        if (isMounted) {
          setDiscountReasons(normalizeCheckoutDiscountReasons(reasons));
        }
      } catch (err) {
        console.warn('Failed to load discount reasons:', err);
        if (isMounted) {
          setDiscountReasons([]);
        }
      }
    };
    fetchDiscountReasons();
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchQuickPickNavigation = useCallback(async () => {
    try {
      if (!activeStore) {
        setQuickPickCategories([]);
        setQuickPickProductActions([]);
        return;
      }
      const response = await inventoryService.fetchProducts(activeStore._id, {
        page: 1,
        limit: 200,
        quickPickOnly: true,
      });
      const navigation = buildProductQuickPickNavigation(response?.data || []);
      setQuickPickCategories(navigation.categories);
      setQuickPickProductActions(navigation.products);
    } catch (err) {
      console.error('Failed to load quick pick navigation', err);
      setQuickPickCategories([]);
      setQuickPickProductActions([]);
    }
  }, [activeStore]);

  useEffect(() => {
    fetchQuickPickNavigation();
  }, [fetchQuickPickNavigation]);

  useEffect(() => {
    if (!quickPickCategory) {
      if (quickPickInitialSubcategory) {
        setQuickPickInitialSubcategory('');
      }
      return;
    }
    if (!quickPickInitialSubcategory) {
      return;
    }
    const exists = (Array.isArray(quickPickCategory.subcategories) ? quickPickCategory.subcategories : [])
      .some(
        (entry) =>
          typeof entry === 'string'
          && entry.trim().toLowerCase() === quickPickInitialSubcategory.trim().toLowerCase()
      );
    if (!exists) {
      setQuickPickInitialSubcategory('');
    }
  }, [quickPickCategory, quickPickInitialSubcategory]);

  useEffect(() => {
    const loadQuickPickProducts = async () => {
      if (!quickPickCategory || !activeStore) {
        setQuickPickProducts([]);
        setQuickPickError('');
        return;
      }
      if (quickPickSelectedProduct) {
        setQuickPickProducts([quickPickSelectedProduct]);
        setQuickPickError('');
        return;
      }
      try {
        setQuickPickLoading(true);
        setQuickPickError('');
        const categoryName = (quickPickCategory?.name || '').trim();
        const subcategoryName = quickPickInitialSubcategory.trim();
        const response = await inventoryService.fetchProducts(
          activeStore._id,
          buildQuickPickProductQuery({ category: categoryName, subcategory: subcategoryName })
        );
        const items = response?.data || [];
        const normalizedCategory = categoryName.toLowerCase();
        const filtered = normalizedCategory
          ? items.filter((product) => {
              const rawCategory =
                typeof product.category === 'string'
                  ? product.category
                  : product.category?.name;
              if (!rawCategory) return false;
              return rawCategory.trim().toLowerCase() === normalizedCategory;
            })
          : items;
        setQuickPickProducts(filtered);
      } catch (err) {
        console.error('Failed to load quick pick products', err);
        setQuickPickError('Unable to load quick pick products.');
        setQuickPickProducts([]);
      } finally {
        setQuickPickLoading(false);
      }
    };
    loadQuickPickProducts();
  }, [quickPickCategory, quickPickInitialSubcategory, quickPickSelectedProduct, activeStore]);

  const handleQuickPickSelect = useCallback((selection) => {
    if (!selection) return;
    if (selection.product) {
      setQuickPickSelectedProduct(selection.product);
      setQuickPickCategory({ name: 'Quick pick', subcategories: [] });
      setQuickPickInitialSubcategory('');
      return;
    }
    setQuickPickSelectedProduct(null);
    if (selection.category && selection.subcategory) {
      setQuickPickCategory(selection.category);
      setQuickPickInitialSubcategory(selection.subcategory);
      return;
    }
    setQuickPickCategory(selection);
    setQuickPickInitialSubcategory('');
  }, []);

  // --- THE FIX IS HERE: The preview now depends on the selected customer ---
  useEffect(() => {
    const pendingPrice = cart.some((item) => item.requiresPrice);
    if (cart.length > 0 && activeStore && !pendingPrice) {
      const cartItems = buildCheckoutSaleItems(cart);
      const customerId = selectedCustomer ? selectedCustomer._id : null;
      salesService
        .previewSale(cartItems, activeStore._id, customerId, selectedPromotionIds)
        .then(setSalePreview)
        .catch(() => setError('Could not calculate discounts.'));
    } else {
      setSalePreview(null);
    }
  }, [cart, activeStore, selectedCustomer, selectedPromotionIds]);
  // --- END OF FIX ---

  useEffect(() => {
    const thisTillId = localStorage.getItem('thisTillId');
    if (socket && thisTillId) {
      socket.emit('register_till', thisTillId);
    }
  }, [socket]);

  const buildStoreInfo = useCallback(() => {
    if (!activeStore) return null;
    return {
      companyName: activeStore.companyName || activeStore.name,
      storeName: activeStore.name,
    };
  }, [activeStore]);

  const sendSaleStateToCfd = useCallback(() => {
    const storeInfo = buildStoreInfo();
    const canUseSocket = socket && tillId;

    if (salePreview && salePreview.items && salePreview.items.length > 0) {
      if (canUseSocket) {
        socket.emit('sale:update_for_cfd', { tillId, saleData: salePreview, storeInfo });
        if (process.env.NODE_ENV !== 'production') {
          console.log('[POS] sale:update_for_cfd', { tillId, storeInfo, items: salePreview.items?.length });
        }
      }
      updateCustomerDisplay(salePreview, storeInfo);
      return;
    }

    if (cart.length === 0) {
      if (canUseSocket) {
        socket.emit('cfd:clear', { tillId, storeInfo });
        if (process.env.NODE_ENV !== 'production') {
          console.log('[POS] cfd:clear', { tillId, storeInfo });
        }
      }
      clearCustomerDisplay(storeInfo);
    } else {
      showIdleCustomerDisplay(storeInfo);
    }
  }, [socket, tillId, salePreview, cart.length, buildStoreInfo]);

  useEffect(() => {
    sendSaleStateToCfd();
  }, [sendSaleStateToCfd]);

  useEffect(() => {
    if (activeStore && socket && tillId) {
      sendSaleStateToCfd();
    }
  }, [activeStore, socket, tillId, sendSaleStateToCfd]);

  useEffect(() => {
    if (!socket || !tillId) return;

    const handleCfdPaired = () => {
      setIsCfdPaired(true);
      sendSaleStateToCfd();
    };

    socket.on('cfd:paired', handleCfdPaired);
    return () => {
      setIsCfdPaired(false);
      socket.off('cfd:paired', handleCfdPaired);
    };
  }, [socket, tillId, sendSaleStateToCfd]);

  useEffect(() => {
    if (isCustomerModalOpen) {
      searchInputRef.current?.blur();
    }
  }, [isCustomerModalOpen]);

  useEffect(() => {
    return () => {
      const storeInfo = buildStoreInfo();
      if (socket && tillId) {
        socket.emit('cfd:clear', { tillId, storeInfo });
      }
      clearCustomerDisplay(storeInfo);
    };
  }, [socket, tillId, buildStoreInfo]);

  const addToCart = (product, initialQuantity = 1) => {
    if (!product) return;
    const resolvedPrice = resolveProductPrice(product);
    const skuUpper = typeof product?.sku === 'string' ? product.sku.trim().toUpperCase() : '';
    const nameUpper = typeof product?.name === 'string' ? product.name.trim().toUpperCase() : '';
    const isMiscProduct =
      Number(resolvedPrice) <= 0 ||
      (skuUpper && skuUpper.includes('MISC')) ||
      (nameUpper && nameUpper.includes('MISC'));
    const requiresPrice = isMiscProduct || Number.isNaN(resolvedPrice) || resolvedPrice <= 0;
    if (requiresPrice) {
      const lineId = `${product._id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setPriceModalItem({
        lineId,
        productId: product._id,
        name: product.name,
        quantity: initialQuantity,
        stock: product.stock,
        gstApplicable: product.gstApplicable !== false,
        isMiscProduct,
      });
      setSearchTerm('');
      setSearchResults([]);
      setSearchSelectionIndex(-1);
      searchInputRef.current?.blur();
      return;
    }

    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.productId === product._id);
      if (existingItem) {
        return prevCart.map(item =>
          item.productId === product._id ? { ...item, quantity: item.quantity + initialQuantity } : item
        );
      }
      return [
        ...prevCart,
        {
          productId: product._id,
          name: product.name,
          price: resolvedPrice,
          basePrice: resolvedPrice,
          quantity: initialQuantity,
          stock: product.stock,
          requiresPrice: false,
          overridePrice: null,
          discountType: null,
          discountValue: null,
          priceSource: 'catalog',
          gstApplicable: product.gstApplicable !== false,
        },
      ];
    });
    // Clear the search/barcode input after adding an item to speed up next scan
    setSearchTerm('');
    setSearchResults([]);
    setSearchSelectionIndex(-1);
  };

  useEffect(() => {
    let showSub;
    let hideSub;
    let viewport;
    const handleViewportResize = () => {
      const vv = window.visualViewport;
      if (!vv) return;
      const offset = Math.max(0, window.innerHeight - vv.height);
      setKeyboardOffset(offset);
    };

    if (Capacitor?.isNativePlatform?.()) {
      showSub = Keyboard.addListener('keyboardWillShow', (info) => {
        setKeyboardOffset(info.keyboardHeight || 0);
        searchInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      hideSub = Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardOffset(0);
      });
    } else if (typeof window !== 'undefined' && window.visualViewport) {
      viewport = window.visualViewport;
      viewport.addEventListener('resize', handleViewportResize);
      handleViewportResize();
    }

    return () => {
      showSub?.remove();
      hideSub?.remove();
      if (viewport) {
        viewport.removeEventListener('resize', handleViewportResize);
      }
    };
  }, []);

  const handleSearchKeyNavigation = (event) => {
    if (!searchResults.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSearchSelectionIndex((prev) => (prev + 1) % searchResults.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSearchSelectionIndex((prev) => (prev <= 0 ? searchResults.length - 1 : prev - 1));
    } else if (event.key === 'Enter') {
      if (searchSelectionIndex >= 0 && searchSelectionIndex < searchResults.length) {
        event.preventDefault();
        addToCart(searchResults[searchSelectionIndex]);
      }
    }
  };

  const updateCartQuantity = (itemKey, quantity) => {
    const safeQuantity = Number.isFinite(quantity) ? quantity : 0;
    if (safeQuantity <= 0) {
      removeFromCart(itemKey);
      return;
    }
    setCart(prev =>
      prev.map(item => (getCartItemKey(item) === itemKey ? { ...item, quantity: safeQuantity } : item))
    );
  };

  const removeFromCart = (itemKey) => {
    setCart(prev => prev.filter(item => getCartItemKey(item) !== itemKey));
  };

  const handleAdjustItem = (itemKey, payload) => {
    setCart((prev) =>
      prev.map((item) => {
        if (getCartItemKey(item) !== itemKey) return item;
        const basePrice = Number.isFinite(item.basePrice) ? item.basePrice : item.price || 0;
        const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
        const isManualPrice = item.priceSource === 'manual' && Number.isFinite(item.basePrice);

        if (payload.mode === 'percent' || payload.mode === 'amount') {
          const lineTotal = basePrice * quantity;
          let discountAmount = 0;
          if (payload.mode === 'percent') {
            discountAmount = (lineTotal * Number(payload.discountValue || 0)) / 100;
          } else {
            const perItemDiscount = Math.min(basePrice, Number(payload.discountValue || 0));
            discountAmount = perItemDiscount * quantity;
          }
          const safeDiscount = Math.min(Math.max(discountAmount, 0), lineTotal);
          const adjustedTotal = Math.max(0, lineTotal - safeDiscount);
          const adjustedUnitPrice = quantity > 0 ? adjustedTotal / quantity : 0;

          return {
            ...item,
            discountType: payload.mode,
            discountValue: payload.discountValue,
            discountReasonId: resolveCartDiscountReasonId(payload),
            overridePrice: adjustedUnitPrice,
            price: adjustedUnitPrice,
            priceSource: item.priceSource || 'discount',
            basePrice: basePrice,
          };
        }
        if (payload.mode === 'override') {
          return {
            ...item,
            overridePrice: payload.overridePrice,
            price: payload.overridePrice,
            priceSource: item.priceSource || 'manual',
            discountType: null,
            discountValue: null,
            discountReasonId: resolveCartDiscountReasonId(payload),
          };
        }
        return {
          ...item,
          discountType: null,
          discountValue: null,
          discountReasonId: resolveCartDiscountReasonId(payload),
          overridePrice: isManualPrice ? basePrice : null,
          price: isManualPrice ? basePrice : basePrice,
          priceSource: isManualPrice ? item.priceSource : 'catalog',
        };
      })
    );
  };

  const handlePriceConfirm = (productId, priceValue, itemInfo = null) => {
    if (itemInfo?.lineId) {
      setCart(prev => [
        ...prev,
        {
          lineId: itemInfo.lineId,
          productId,
          name: itemInfo.name,
          price: priceValue,
          basePrice: priceValue,
          quantity: itemInfo.quantity || 1,
          stock: itemInfo.stock,
          requiresPrice: false,
          overridePrice: priceValue,
          discountType: null,
          discountValue: null,
          priceSource: 'manual',
          gstApplicable: itemInfo.gstApplicable,
          miscDescription: itemInfo.miscDescription || '',
          miscBarcode: itemInfo.miscBarcode || '',
        },
      ]);
    } else if (itemInfo && !cart.find((item) => item.productId === productId)) {
      setCart(prev => [
        ...prev,
        {
          productId,
          name: itemInfo.name,
          price: priceValue,
          basePrice: priceValue,
          quantity: itemInfo.quantity || 1,
          stock: itemInfo.stock,
          requiresPrice: false,
          overridePrice: priceValue,
          discountType: null,
          discountValue: null,
          priceSource: 'manual',
          gstApplicable: itemInfo.gstApplicable,
          miscDescription: itemInfo.miscDescription || '',
          miscBarcode: itemInfo.miscBarcode || '',
        },
      ]);
    } else {
      setCart(prev =>
        prev.map(item =>
          item.productId === productId
            ? {
                ...item,
                price: priceValue,
                basePrice: item.basePrice ?? priceValue,
                overridePrice: priceValue,
                requiresPrice: false,
                priceSource: 'manual',
              }
            : item
        )
      );
    }
    setPriceModalItem(null);
  };

  const handlePriceCancel = (itemKey) => {
    removeFromCart(itemKey);
    setPriceModalItem(null);
  };

  const handleFinalizeSale = async (paymentMethod, stripePaymentIntentId = null, meta = {}) => {
    if (saleFinalizationInFlightRef.current) {
      const message = 'Sale is already being processed. Wait for the receipt before trying again.';
      setError(message);
      throw new Error(message);
    }
    if (cart.some(item => item.requiresPrice)) {
      setError('Please enter prices for highlighted items before completing the sale.');
      return;
    }
    saleFinalizationInFlightRef.current = true;
    setIsFinalizingSale(true);
    try {
      const tillIdParam = meta?.tillId || null;
      const saleData = {
        items: buildCheckoutSaleItems(cart),
        paymentMethod,
        customerId: selectedCustomer ? selectedCustomer._id : null,
        stripePaymentIntentId,
        loyaltyRedemption: loyaltyRedemption.points > 0 ? loyaltyRedemption : undefined,
        tillId: tillIdParam || undefined,
        selectedPromotionIds,
        storeId: activeStore?._id,
        idempotencyKey: checkoutIdempotencyKeyRef.current,
        ...(meta?.paymentSource ? { paymentSource: meta.paymentSource } : {}),
        ...(meta?.terminalReference ? { terminalReference: meta.terminalReference } : {}),
        ...(meta?.cashTendered != null ? { cashTendered: meta.cashTendered } : {}),
        ...(meta?.cashChange != null ? { cashChange: meta.cashChange } : {}),
        ...(Array.isArray(meta?.paymentAllocations) && meta.paymentAllocations.length > 0
          ? { paymentAllocations: meta.paymentAllocations }
          : {}),
      };
      const newSale = await salesService.createSale(saleData);
      
      setCompletedSale(newSale);
      setReceiptModalOpen(true);

      clearCart();
      setCheckoutNotice(null);
      setPaymentModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to process sale.');
      setPaymentModalOpen(false);
    } finally {
      saleFinalizationInFlightRef.current = false;
      setIsFinalizingSale(false);
    }
  };

  const handleCreateB2BOrder = async () => {
    if (!activeStore?._id) {
      setError('Please select a store before creating a B2B order.');
      return;
    }
    if (!canCreateB2BOrder) {
      setError('B2B checkout conversion is not enabled for this store or your role.');
      return;
    }
    if (!cart.length) {
      setError('Add at least one item before creating a B2B order.');
      return;
    }
    if (hasUnpricedItems) {
      setError('Please enter prices for highlighted items before creating a B2B order.');
      return;
    }
    if (!selectedCustomer?._id) {
      setError('Attach a customer before creating a B2B order.');
      return;
    }

    setError('');
    setCheckoutNotice(null);
    setIsCreatingB2BOrder(true);
    try {
      const payload = {
        customerId: selectedCustomer._id,
        storeId: activeStore._id,
        items: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.price,
          discountType: item.discountType,
          discountValue: item.discountValue,
          miscDescription: item.miscDescription,
          miscBarcode: item.miscBarcode,
          ...(item.overridePrice != null ? { overridePrice: item.overridePrice } : {}),
        })),
      };

      const createdOrder = await b2bOrderService.createOrderFromCheckout(payload);
      clearCart();
      setCheckoutNotice({
        orderNumber: createdOrder?.orderNumber || '',
      });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create B2B order from checkout.');
    } finally {
      setIsCreatingB2BOrder(false);
    }
  };

  const handleRedeemPoints = async () => {
    if (!selectedCustomer || !selectedCustomer.loyaltyCard) return;
    const maxPoints = Number(selectedCustomer.loyaltyCard.pointsBalance || 0);
    const maxDollarValue = loyaltyRatio > 0 ? maxPoints / loyaltyRatio : 0;
    const pointsToRedeem = parseInt(
      prompt(`How many points to redeem? (Max: ${maxPoints} = $${maxDollarValue.toFixed(2)})`, maxPoints),
      10
    );
    if (pointsToRedeem > 0) {
        try {
            const redemptionData = await loyaltyService.redeemPoints(selectedCustomer._id, pointsToRedeem);
            setLoyaltyRedemption({
                points: redemptionData.pointsRedeemed,
                discountAmount: redemptionData.discountAmount
            });
            if (loyaltyOriginalBalance == null) {
              setLoyaltyOriginalBalance(maxPoints);
            }
            const nextBalance = Math.max(0, maxPoints - redemptionData.pointsRedeemed);
            setSelectedCustomer((prev) => {
              if (!prev?.loyaltyCard) return prev;
              return {
                ...prev,
                loyaltyCard: {
                  ...prev.loyaltyCard,
                  pointsBalance: nextBalance,
                },
              };
            });
        } catch (err) {
            setError(err.response?.data?.message || "Failed to redeem points.");
        }
    }
  };

  const total =
    (salePreview ? salePreview.total : cart.reduce((acc, item) => acc + item.price * item.quantity, 0)) -
    loyaltyRedemption.discountAmount;
  const hasUnpricedItems = cart.some((item) => item.requiresPrice);

  const handleBarcodeScan = async (scannedValue) => {
    if (!activeStore) return;
    try {
      const parsed = /^(.+)\|x(\d+)$/i.exec(scannedValue.trim());
      const lookupCode = parsed ? parsed[1] : scannedValue;
      const quantityFromScan = parsed ? Math.max(1, parseInt(parsed[2], 10)) : 1;
      const results = await inventoryService.searchProducts(activeStore._id, lookupCode);
      const product = results.find(
        (p) => p.barcode === lookupCode || p.sku === lookupCode
      ) || results[0];
      if (product) {
        addToCart(product, quantityFromScan);
        return;
      }
    } catch (err) {
      console.error('Barcode search failed:', err);
    }
    setFailedSku(scannedValue);
    setIsSkuModalOpen(true);
  };
  
  useBarcodeScanner(handleBarcodeScan);

  const togglePromotion = (promo) => {
    setPromoMessage('');
    setSelectedPromotionIds(prev => {
      const isSelected = prev.includes(promo._id);
      if (isSelected) {
        return prev.filter(id => id !== promo._id);
      }
      if (!promo.isStackable) {
        return [promo._id];
      }
      const hasExclusive = promotions.some(p => prev.includes(p._id) && !p.isStackable);
      if (hasExclusive) {
        setPromoMessage('Remove the exclusive promotion before stacking others.');
        return prev;
      }
      return [...prev, promo._id];
    });
  };

  useEffect(() => {
    if (!searchTerm?.trim()) {
      setSearchResults([]);
      return;
    }
    const debounce = setTimeout(() => runRemoteSearch(searchTerm), 250);
    return () => clearTimeout(debounce);
  }, [searchTerm, runRemoteSearch]);

  const renderPromotionChips = () => {
    if (!promotions?.length) {
      return <span className="text-sm text-slate-400">No active promotions.</span>;
    }
    return (
      <div className="flex items-center gap-2 overflow-x-auto pb-1 hide-scrollbar">
        {promotions.map((promo) => {
          const selected = selectedPromotionIds.includes(promo._id);
          return (
            <button
              key={promo._id}
              onClick={() => togglePromotion(promo)}
              className={`px-4 py-2 rounded-full border text-sm whitespace-nowrap transition-colors ${
                selected
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-primary/40'
              }`}
            >
              <span className="font-semibold mr-2 text-slate-900">{promo.name}</span>
              <span className="text-xs uppercase tracking-wide text-slate-500">
                {promo.type === 'PERCENTAGE_ORDER' || promo.type === 'PERCENTAGE_ITEM'
                  ? `${promo.value?.discountValue || 0}% off`
                  : promo.type?.includes('FIXED')
                  ? `$${promo.value?.discountValue || 0} off`
                  : 'Promo'}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  if (showTerminalSetup) {
    return (
      <div className="min-h-full bg-slate-50 flex flex-col">
      <div className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200">
          <div className="swan-main-shell py-3 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Checkout</p>
              <p className="text-lg font-semibold text-slate-900 mt-1">Configure this terminal</p>
              <p className="text-sm text-slate-500">Select a till for this device before processing sales.</p>
            </div>
            <button
              onClick={() => navigate('/terminal-setup')}
              className="px-4 py-2 text-sm font-semibold rounded-full border border-primary text-primary hover:bg-primary hover:text-white"
            >
              Go to Terminal Setup
            </button>
          </div>
        </div>
        <TerminalSetupPage />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 flex flex-col">
      <CashierQuickActions
        context="checkout"
        quickPickCategories={quickPickCategories}
        quickPickProducts={quickPickProductActions}
        onQuickPick={handleQuickPickSelect}
      />
      {showTillClosedModal && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">Open a till session</h2>
            <p className="text-sm text-slate-600">
              This terminal has no open till session. Please open a till before processing sales.
            </p>
            {tillCheckError && <p className="text-xs text-rose-600">{tillCheckError}</p>}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowTillClosedModal(false)}
                className="px-4 py-2 rounded-full border border-slate-300 text-sm font-semibold text-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={() => navigate('/tills')}
                className="px-4 py-2 rounded-full bg-primary text-white text-sm font-semibold"
              >
                Open till
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="swan-main-shell h-full flex flex-col lg:flex-row gap-4 lg:gap-6 py-4 overflow-hidden">
        <section className="flex-1 bg-white rounded-3xl swan-panel swan-scroll-card p-4 lg:p-5 flex flex-col h-full min-h-0 overflow-hidden">
          <div className="mb-5 flex flex-col gap-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Checkout</p>
                <p className="text-xl font-semibold text-slate-900 mt-1">{activeStore?.name || 'Select a store'}</p>
                <p className="text-sm text-slate-500">
                  Terminal {tillName || (tillId ? `#${tillId.slice(-6)}` : 'not configured')}
                  {tillId
                    ? ` • Till #${tillNumber || tillId.slice(-6)}`
                    : ''} • CFD {isCfdPaired ? 'ready' : 'waiting'}
                </p>
              </div>
              {selectedCustomer && (
                <button
                  onClick={() => {
                    setSelectedCustomer(null);
                    setLoyaltyRedemption({ points: 0, discountAmount: 0 });
                  }}
                  className="text-slate-500 hover:text-rose-500 text-sm font-semibold"
                >
                  Remove customer
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Active promotions</p>
              {renderPromotionChips()}
            </div>
          </div>
          {error && <p className="bg-rose-100 text-rose-700 border border-rose-200 px-4 py-2 rounded-2xl mb-4">{error}</p>}
          {checkoutNotice && (
            <p className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-4 py-2 rounded-2xl mb-4">
              B2B order{' '}
              {checkoutNotice.orderNumber ? (
                <Link
                  to={`/b2b-orders?orderNumber=${encodeURIComponent(checkoutNotice.orderNumber)}`}
                  className="font-semibold underline hover:text-emerald-900"
                >
                  {checkoutNotice.orderNumber}
                </Link>
              ) : (
                'created'
              )}{' '}
              created successfully.
            </p>
          )}
          <div className="mb-4">
            <ProductSearchInput
              value={searchTerm}
              onProductSelect={addToCart}
              onSearchChange={setSearchTerm}
              onNavigateResults={handleSearchKeyNavigation}
              inputRef={searchInputRef}
              onHideKeyboard={() => searchInputRef.current?.blur()}
            />
            <p className="text-[11px] text-slate-500 mt-1">Scan or type SKU / barcode to add items instantly.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-slate-600">
            <span className="font-semibold">Search:</span>
            <span className="px-3 py-1 rounded-full bg-slate-100">{searchTerm || 'Type to search or scan'}</span>
            {searchLoading && <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700">Searching…</span>}
          </div>
          <div
            className="flex-1 overflow-y-auto pr-1"
            style={keyboardOffset ? { paddingBottom: keyboardOffset + 16 } : undefined}
          >
            {!searchTerm ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm text-center px-6">
                Type a product name/SKU or scan a barcode to fetch products. We no longer load all items to keep checkout fast with large catalogs.
              </div>
            ) : searchResults.length === 0 && !searchLoading ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-lg">
                No products match your search.
              </div>
            ) : (
              <div className="flex flex-col divide-y divide-slate-100">
                {searchResults.map((product, idx) => (
                  <button
                    key={product._id}
                    onClick={() => addToCart(product)}
                    className={`text-left py-3 px-2 flex items-center justify-between transition ${
                      idx === searchSelectionIndex ? 'bg-slate-100' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900 line-clamp-2 text-base">{product.name}</p>
                      <p className="text-xs text-slate-500">
                        SKU: {product.sku} • Barcode: {product.barcode || '—'}
                      </p>
                    </div>
                    <div className="text-right min-w-[120px]">
                      <p className="mt-1 text-primary font-bold text-lg">
                        $
                        {Number.isFinite(product.price)
                          ? product.price.toFixed(2)
                          : Number(product.basePrice || 0).toFixed(2)}
                      </p>
                      <p className={`text-xs ${product.stock < 10 ? 'text-rose-500 font-semibold' : 'text-slate-500'}`}>
                        Stock: {product.stock}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <aside className="w-full lg:w-[420px] flex flex-col h-full min-h-0">
          {promoMessage && <span className="text-sm text-amber-600 mb-2">{promoMessage}</span>}
          <div className="flex-1 bg-white rounded-3xl swan-panel swan-scroll-card p-4 overflow-hidden min-h-0">
            <Cart
              cart={cart}
              salePreview={salePreview}
              loyaltyDiscount={loyaltyRedemption.discountAmount}
              loyaltyPoints={loyaltyRedemption.points}
              selectedPromotions={
                salePreview?.appliedPromotions?.length
                  ? salePreview.appliedPromotions
                  : promotions
                      .filter((promo) => selectedPromotionIds.includes(promo._id))
                      .map((promo) => ({
                        promoId: promo._id,
                        name: promo.name,
                      }))
              }
              onRemovePromotion={(promoId) => {
                setSelectedPromotionIds((prev) => prev.filter((id) => id !== promoId));
                setPromoMessage('');
              }}
          onUpdateQuantity={updateCartQuantity}
          onAdjustItem={handleAdjustItem}
          onRemoveItem={removeFromCart}
              onClearCart={clearCart}
              onProcessPayment={() => {
                setTyroReceipts(null);
                setPaymentModalOpen(true);
              }}
              onCreateB2BOrder={handleCreateB2BOrder}
              onAddCustomer={() => setCustomerModalOpen(true)}
              onRedeemPoints={handleRedeemPoints}
              onClearLoyaltyRedemption={() => {
                setLoyaltyRedemption({ points: 0, discountAmount: 0 });
                if (loyaltyOriginalBalance != null) {
                  setSelectedCustomer((prev) => {
                    if (!prev?.loyaltyCard) return prev;
                    return {
                      ...prev,
                      loyaltyCard: {
                        ...prev.loyaltyCard,
                        pointsBalance: loyaltyOriginalBalance,
                      },
                    };
                  });
                  setLoyaltyOriginalBalance(null);
                }
              }}
              selectedCustomer={selectedCustomer}
              hasUnpricedItems={hasUnpricedItems}
              discountReasons={discountReasons}
              canApplyDiscount={canApplyDiscount}
              canCreateB2BOrder={canCreateB2BOrder}
              isCreatingB2BOrder={isCreatingB2BOrder}
              onRemoveCustomer={() => {
                setSelectedCustomer(null);
                setLoyaltyRedemption({ points: 0, discountAmount: 0 });
                setLoyaltyOriginalBalance(null);
              }}
            />
          </div>
        </aside>
        </div>
      </div>

      {isCustomerModalOpen && (
        <CustomerSearchModal
          onSelectCustomer={(customer) => {
            setSelectedCustomer(customer);
            setLoyaltyRedemption({ points: 0, discountAmount: 0 });
            setLoyaltyOriginalBalance(null);
            setCustomerModalOpen(false);
          }}
          onCancel={() => setCustomerModalOpen(false)}
        />
      )}

      {isPaymentModalOpen && (
        <PaymentModal 
          total={total}
          onCancel={() => {
            setTyroReceipts(null);
            setPaymentModalOpen(false);
          }}
          onFinalizeSale={handleFinalizeSale}
          onTyroReceipt={setTyroReceipts}
          saleFinalizing={isFinalizingSale}
        />
      )}
      
      {isSkuModalOpen && (
        <SkuSearchModal
          initialSku={failedSku}
          activeStore={activeStore}
          onCancel={() => setIsSkuModalOpen(false)}
          onProductSelect={(product) => {
            addToCart(product);
            setIsSkuModalOpen(false);
          }}
        />
      )}

      {isReceiptModalOpen && completedSale && (
        <ReceiptModal
          sale={completedSale}
          store={completedSale.store}
          tyroReceipts={tyroReceipts}
          onClose={() => {
            setReceiptModalOpen(false);
            setTyroReceipts(null);
          }}
        />
      )}
      {priceModalItem && (
        <PriceEntryModal
          item={priceModalItem}
          onConfirm={handlePriceConfirm}
          onCancel={handlePriceCancel}
        />
      )}
      {quickPickCategory && (
        <QuickPickModal
          category={quickPickCategory}
          initialSubcategory={quickPickInitialSubcategory}
          products={quickPickProducts}
          isLoading={quickPickLoading}
          error={quickPickError}
          onClose={() => {
            setQuickPickCategory(null);
            setQuickPickInitialSubcategory('');
            setQuickPickSelectedProduct(null);
          }}
          onAdd={(product, quantity) => addToCart(product, quantity)}
        />
      )}
    </div>
  );
};

export default CheckoutPage;
